import React from 'react';
import { Row, Col, Tooltip, Divider } from 'antd';
import { ExpandNavibox, HalfPage, Rectangle, SkyscraperMobile, Wideboard } from 'components/Ads';
import { useRouter } from 'next/router';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import { SectionTitle } from 'components/Section';
import CompanyImage from 'components/CompanyImage';
import GalleryCard from 'components/GalleryCard';
import VideoCard from 'components/VideoCard';
import Subscription from 'containers/Subscription';
import Subtitle from 'components/SubTitle';
import Pagination from 'components/Pagination';
import WhitePaperCard from 'components/WhitePaperCard';
import Section from 'components/Section';
import Item from 'components/List/ArticleItem';
import Link from 'next/link';
import Image from 'components/Image';
import CardGrid from 'components/CardGrid';

export interface Props {
    ads: PageAds;
    articles: {
        items: Article[];
        results: number;
        currentPage: number;
        numberOfPages: number;
        next: boolean;
        prev: boolean;
        paginate: boolean;
    };
    category: Category;
    singleGallery: Gallery;
    singleVideo: Video;
    latestIssue: Issue;
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    whitePaper: WhitePaper;
}

export const SubCategoryPage = ({
    ads,
    articles,
    category,
    singleGallery,
    singleVideo,
    latestIssue,
    events,
    eventsPatronate,
    whitePaper
}: Props): React.JSX.Element => {
    const router = useRouter();
    const { items, currentPage, results, next, prev } = articles || {
        items: [],
        currentPage: 1,
        results: 0,
        next: false,
        prev: false
    };

    const onChange = (page) => {
        const path: string[] = router.query.all as string[];
        router.push(`${path.join('/')}?page=${page}`);
    };

    const { rectangle, skyscraper, halfpage, navibox, wideboardBottom } = ads;
    return (
        <>
            <Row gutter={16}>
                <Content>
                    <Row align="bottom">
                        <Col xs={category.patron ? 12 : 24} md={24}>
                            <SectionTitle title={`Najnowsze z ${category.title}`} />
                        </Col>
                        {category.patron && (
                            <Col xs={12} md={0}>
                                <Row justify="end">
                                    <Col>
                                        <SectionTitle title="Patron branży" />
                                    </Col>
                                    <Col span={16}>
                                        <CompanyImage small={true} patron={category.patron} />
                                    </Col>
                                </Row>
                            </Col>
                        )}
                    </Row>
                    <Row gutter={[16, 16]}>
                        {items.map((item: Article, i: number) => {
                            if (i === 3) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <Item $article={item} />
                                        <Row justify="center">
                                            <Col xs={24} sm={12} xl={0}>
                                                <SkyscraperMobile
                                                    id={skyscraper[0]}
                                                    margin="12px auto 12px auto"
                                                />
                                            </Col>
                                            <Col xs={24} sm={12} xl={0}>
                                                <SkyscraperMobile
                                                    id={skyscraper[1]}
                                                    margin="12px auto 0 auto"
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                );
                            }
                            if (i === 7) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <Item $article={item} />
                                        <ExpandNavibox id={navibox[0]} margin="25px auto" />
                                    </Col>
                                );
                            }
                            if (i === 13) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <Item $article={item} />
                                        <ExpandNavibox id={navibox[1]} margin="25px auto" />
                                    </Col>
                                );
                            }

                            return (
                                <Col span={24} key={item.seoSlug}>
                                    <Item $article={item} />
                                </Col>
                            );
                        })}
                    </Row>
                    <Section justify="center">
                        <Col span={24}>
                            <Pagination
                                currentPage={currentPage}
                                results={results}
                                onChange={onChange}
                                next={next}
                                prev={prev}
                            />
                        </Col>
                    </Section>
                </Content>
                <Sider>
                    {category.patron && (
                        <Row>
                            <Col xs={0} md={24}>
                                <SectionTitle title="Patron branży" />
                            </Col>
                            <Col xs={0} md={24}>
                                <CompanyImage patron={category.patron} />
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col span={24}>
                            <Rectangle id={rectangle[0]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[0]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[1]} margin="16px auto" />
                        </Col>
                        {singleGallery && (
                            <Col span={24}>
                                <SectionTitle title="Galeria" url={`/galerie`} />
                                <GalleryCard
                                    images={singleGallery.images}
                                    title={singleGallery.title}
                                    category={singleGallery.category}
                                    slug={singleGallery.seoSlug}
                                />
                            </Col>
                        )}
                        <Col span={24}>
                            <Rectangle id={rectangle[2]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[3]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[1]} margin="16px auto" />
                        </Col>
                        {singleVideo && (
                            <>
                                <Col span={24}>
                                    <SectionTitle title="Wideo" url={`/wideo`} />
                                </Col>
                                <Col span={24}>
                                    <VideoCard video={singleVideo} />
                                </Col>
                                <Divider />
                            </>
                        )}
                        <Col span={24}>
                            <Rectangle id={rectangle[4]} margin="16px auto" />
                        </Col>
                        {whitePaper && (
                            <>
                                <Col span={24}>
                                    <SectionTitle title="White Paper" url={`/white-papers`} />
                                </Col>
                                <Col span={24}>
                                    <WhitePaperCard item={whitePaper} />
                                </Col>
                                <Divider />
                            </>
                        )}
                        <Col span={24}>
                            <HalfPage id={halfpage[2]} margin="16px auto" />
                        </Col>
                    </Row>
                </Sider>
            </Row>
            <Row gutter={16}>
                {(eventsPatronate || events) && (
                    <Col span={24}>
                        <SectionTitle title="Wydarzenia" url="/wydarzenia" />
                        {eventsPatronate && (
                            <Row>
                                <Col style={{ marginBottom: 17 }}>
                                    <Subtitle category="Nasz patronat" />
                                </Col>
                                <Col span={24}>
                                    <Row justify="center" gutter={[32, 16]}>
                                        {eventsPatronate.map(({ seoSlug, name, logo }) => (
                                            <Col key={`${seoSlug}-patronate`}>
                                                <Link href={`/wydarzenia/${seoSlug}`}>
                                                    <Tooltip title={name}>
                                                        <Image
                                                            type="logo"
                                                            height={96}
                                                            width={96}
                                                            alt={name}
                                                            src={logo}
                                                        />
                                                    </Tooltip>
                                                </Link>
                                            </Col>
                                        ))}
                                    </Row>
                                </Col>
                            </Row>
                        )}
                        {events && (
                            <Section>
                                <Col>
                                    <Subtitle
                                        category={
                                            <Link href="/wydarzenia">
                                                Najbliższe wydarzenia{' '}
                                                <small style={{ paddingLeft: 10, fontSize: 14 }}>
                                                    wiecej
                                                </small>
                                            </Link>
                                        }
                                    />
                                </Col>
                                <Col span={24}>
                                    <CardGrid
                                        type="event"
                                        noImages
                                        items={events}
                                        maxItems={4}
                                        maxItemsInRow={4}
                                    />
                                </Col>
                            </Section>
                        )}
                        <Row>
                            <Col span={24}>
                                <ExpandNavibox id={ads.navibox[2]} margin="0 auto 24px auto" />
                            </Col>
                        </Row>
                    </Col>
                )}
            </Row>
            <Row gutter={16}>
                <Content>
                    <ExpandNavibox id={navibox[2]} margin="35px auto 0 auto" />
                </Content>
                <Sider>
                    <Rectangle id={rectangle[5]} margin="35px auto 0 auto" />
                </Sider>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Wideboard id={wideboardBottom[0]} />
                </Col>
            </Row>
        </>
    );
};

export default SubCategoryPage;
